import React, { useMemo, useState } from "react";
import classNames from "classnames";

const MONTHS = [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December",
];

// Monday-first weekday labels (the app is Swedish).
const WEEKDAYS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

const pad = (n) => String(n).padStart(2, "0");
const toIso = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;

/**
 * A light-themed month calendar that highlights days containing trips and lets
 * the user pick one. Used by TrainList when the tenant has calendar grouping on.
 *
 * Props:
 *   - trainDates:   array of ISO date strings ("YYYY-MM-DD") that have trips
 *   - selectedDate: the currently selected ISO date (or null)
 *   - onSelectDate: (iso) => void
 */
const TrainCalendar = ({ trainDates, selectedDate, onSelectDate }) => {
    const tripDays = useMemo(() => new Set(trainDates), [trainDates]);

    // Open on the month of the selected date, otherwise the earliest trip,
    // otherwise today.
    const initial = useMemo(() => {
        const anchor = selectedDate || [...tripDays].sort()[0];
        if (anchor) {
            const [y, m] = anchor.split("-").map(Number);
            return { year: y, month: m - 1 };
        }
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [view, setView] = useState(initial);

    const todayIso = useMemo(() => {
        const n = new Date();
        return toIso(n.getFullYear(), n.getMonth(), n.getDate());
    }, []);

    const { year, month } = view;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Convert Sun-first (0..6) to Mon-first leading offset.
    const leading = (new Date(year, month, 1).getDay() + 6) % 7;

    const goToMonth = (delta) => {
        const d = new Date(year, month + delta, 1);
        setView({ year: d.getFullYear(), month: d.getMonth() });
    };

    const cells = [];
    for (let i = 0; i < leading; i++) {
        cells.push(<div key={`pad-${i}`} />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
        const iso = toIso(year, month, day);
        const hasTrips = tripDays.has(iso);
        const isSelected = iso === selectedDate;
        const isToday = iso === todayIso;

        cells.push(
            <button
                key={iso}
                type="button"
                disabled={!hasTrips}
                onClick={() => hasTrips && onSelectDate(iso)}
                aria-pressed={isSelected}
                className={classNames(
                    "relative flex h-11 w-full items-center justify-center rounded-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1",
                    {
                        "bg-indigo-600 font-semibold text-white shadow": isSelected,
                        "cursor-pointer font-semibold text-gray-900 hover:bg-indigo-50": hasTrips && !isSelected,
                        "cursor-default text-gray-300": !hasTrips,
                        "ring-1 ring-indigo-300": isToday && !isSelected,
                    }
                )}
            >
                {day}
                {hasTrips && (
                    <span
                        className={classNames(
                            "absolute bottom-1.5 h-1.5 w-1.5 rounded-full",
                            isSelected ? "bg-white" : "bg-indigo-600"
                        )}
                    />
                )}
            </button>
        );
    }

    return (
        <div className="mx-auto max-w-md rounded-2xl bg-white p-6 shadow-md ring-1 ring-gray-100">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => goToMonth(-1)}
                    className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label="Föregående månad"
                >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h3 className="text-lg font-semibold text-gray-900">
                    {MONTHS[month]} {year}
                </h3>
                <button
                    type="button"
                    onClick={() => goToMonth(1)}
                    className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label="Nästa månad"
                >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            <div className="mt-6 grid grid-cols-7 text-center text-xs font-medium uppercase tracking-wide text-gray-400">
                {WEEKDAYS.map((d) => (
                    <div key={d} className="py-1">{d}</div>
                ))}
            </div>

            <div className="mt-1 grid grid-cols-7 gap-1">
                {cells}
            </div>

            <div className="mt-6 flex items-center justify-center text-xs text-gray-400">
                <span className="mr-2 h-1.5 w-1.5 rounded-full bg-indigo-600" />
                Datum med resor
            </div>
        </div>
    );
};

export default TrainCalendar;
